import { z } from 'zod';
import { QueueTicketStatus } from '../enums.js';
import { IsoDateTime, NonEmptyName, Phone, Uuid } from '../primitives.js';

/**
 * A walk-in queue ticket. `position` and `etaAt` are *computed* from live diary state at
 * read time, never stored as truth. `ticketCode` lets the client watch their place at
 * salonomy.com/q/ticket/<code> with no login.
 */
export const QueueTicket = z
  .object({
    id: Uuid,
    salonId: Uuid,
    ticketCode: z.string().describe('opaque public code for the no-login ticket view'),
    clientId: Uuid.nullable(),
    clientName: NonEmptyName,
    phone: Phone.nullable(),
    serviceId: Uuid.nullable(),
    requestedStaffId: Uuid.nullable().describe('null = any chair'),
    status: QueueTicketStatus,
    joinedAt: IsoDateTime,
    calledAt: IsoDateTime.nullable(),
    notifyLeadMin: z.int().min(0).max(120),
    position: z.int().min(0).describe('1-based position among waiting tickets; 0 if not waiting'),
    etaAt: IsoDateTime.nullable().describe('computed estimated chair time'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'QueueTicket' });
export type QueueTicket = z.infer<typeof QueueTicket>;

/** Staff-side: add someone to the queue (incl. an anonymous walk-in added at the desk). */
export const JoinQueue = z
  .object({
    clientName: NonEmptyName,
    phone: Phone.nullish(),
    clientId: Uuid.nullish(),
    serviceId: Uuid.nullish(),
    requestedStaffId: Uuid.nullish(),
    notifyLeadMin: z.int().min(0).max(120).default(10),
  })
  .meta({ id: 'JoinQueue' });
export type JoinQueue = z.infer<typeof JoinQueue>;

/** Public-side join (from the shared salonomy.com/q/<slug> link). */
export const PublicJoinQueue = z
  .object({
    name: NonEmptyName,
    phone: Phone.nullish(),
    serviceId: Uuid.nullish(),
    requestedStaffId: Uuid.nullish(),
  })
  .meta({ id: 'PublicJoinQueue' });
export type PublicJoinQueue = z.infer<typeof PublicJoinQueue>;

export const QueueAction = z
  .object({
    action: z.enum(['call', 'hold', 'no_show', 'complete', 'requeue']),
  })
  .meta({ id: 'QueueAction' });
export type QueueAction = z.infer<typeof QueueAction>;

/** What a client sees on their own ticket (design screen C2) — minimal PII. */
export const PublicQueueTicket = z
  .object({
    ticketCode: z.string(),
    salonName: z.string(),
    salonSlug: z.string(),
    status: QueueTicketStatus,
    position: z.int().min(0),
    etaAt: IsoDateTime.nullable(),
    serviceName: z.string().nullable(),
    staffName: z.string().nullable(),
    notifyLeadMin: z.int(),
    nowInChair: z.object({ staffName: z.string(), minutesLeft: z.int().nullable() }).nullable(),
  })
  .meta({ id: 'PublicQueueTicket' });
export type PublicQueueTicket = z.infer<typeof PublicQueueTicket>;

/** Public salon queue snapshot (the salonomy.com/q/<slug> board) — aggregate only, no names. */
export const PublicQueueSnapshot = z
  .object({
    salonName: z.string(),
    salonSlug: z.string(),
    open: z.boolean(),
    waitingCount: z.int().min(0),
    avgWaitMin: z.int().min(0).nullable(),
    estimatedJoinEtaAt: IsoDateTime.nullable().describe('ETA if you joined now'),
  })
  .meta({ id: 'PublicQueueSnapshot' });
export type PublicQueueSnapshot = z.infer<typeof PublicQueueSnapshot>;

/** Offer a diary gap to matching waiting tickets (design: "Offer to queue"). */
export const OfferGapToQueue = z
  .object({
    staffId: Uuid,
    startAt: IsoDateTime,
    endAt: IsoDateTime,
    serviceId: Uuid.nullish(),
    /** Limit the offer to the N best-matching tickets. */
    maxOffers: z.int().min(1).max(20).default(5),
  })
  .meta({ id: 'OfferGapToQueue' });
export type OfferGapToQueue = z.infer<typeof OfferGapToQueue>;

export const GapOfferResult = z
  .object({
    offeredTo: z.array(z.object({ ticketId: Uuid, clientName: z.string(), notified: z.boolean() })),
    matchCount: z.int(),
  })
  .meta({ id: 'GapOfferResult' });
export type GapOfferResult = z.infer<typeof GapOfferResult>;

/** Server-Sent Event payloads pushed to live queue subscribers (ADR-0004). */
export const QueueEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('snapshot'), snapshot: PublicQueueSnapshot }),
  z.object({ type: z.literal('ticket'), ticket: PublicQueueTicket }),
  z.object({ type: z.literal('ping'), at: IsoDateTime }),
]);
export type QueueEvent = z.infer<typeof QueueEvent>;
